import { OrganizationIdentifierKindEnum } from '@e-socna/data-layer';
import { EMPTY_STRING } from '@e-socna/empty';
import {
  formatOrganizationIdentifierInput,
  isValidSiret,
} from '@e-socna/third-parties';
import type { Id } from '@e-socna/data-layer';

import { generateId } from '../../../utils/generate-id.util';
import type { AddressForm } from '../../../forms/Address/types/address-form.type';
import type { ThirdPartyForm_NafEntry } from '../../../forms/ThirdParty/hooks/useNafEntriesQuery.hook';

import type {
  OrganizationSearchEstablishment,
  OrganizationSearchResult,
} from '../types/organization-search-result.type';

import { matchLegalStatusFromNatureJuridique } from './legal-status-code.mapper';

export interface LegalStatusForMatching {
  readonly _id: Id;
  readonly inseeCode: string;
}

export interface MappingContext {
  readonly legalStatuses: readonly LegalStatusForMatching[];
  readonly nafIdsByCode: Map<string, Id>;
}

export const resolveLegalStatusId = (
  result: OrganizationSearchResult,
  { legalStatuses }: Pick<MappingContext, 'legalStatuses'>,
): Id | undefined =>
  matchLegalStatusFromNatureJuridique(result.nature_juridique, legalStatuses)
    ?._id;

export const resolveTradingName = (
  result: OrganizationSearchResult,
  establishment: Readonly<OrganizationSearchEstablishment>,
) =>
  establishment.nom_commercial ??
  establishment.liste_enseignes?.[0] ??
  result.sigle ??
  undefined;

export const buildIdentifier = (
  result: OrganizationSearchResult,
  establishment: Readonly<OrganizationSearchEstablishment>,
) => {
  const rawValue =
    establishment.siret && isValidSiret(establishment.siret)
      ? establishment.siret
      : result.siren;

  return {
    __typename: 'OrganizationIdentifier' as const,
    _id: generateId(),
    kind:
      rawValue.length > result.siren.length
        ? OrganizationIdentifierKindEnum.Siret
        : OrganizationIdentifierKindEnum.Siren,
    value: formatOrganizationIdentifierInput(rawValue),
  };
};

export const buildNafIdsByCode = (
  nafEntries: readonly Pick<ThirdPartyForm_NafEntry, '_id' | 'code'>[],
): Map<string, Id> =>
  new Map(nafEntries.map((nafEntry) => [nafEntry.code, nafEntry._id]));

export const buildAddressFromEstablishment = (
  establishment: Readonly<OrganizationSearchEstablishment>,
  { addressTypeId }: Readonly<{ addressTypeId: Id }>,
) => {
  const route = [establishment.type_voie, establishment.libelle_voie]
    .filter(Boolean)
    .join(' ');

  return [
    {
      __typename: 'Address' as const,
      _id: generateId(),
      typeIds: [addressTypeId],
      locationInfo: null,
      cadastralInfo: null,
      postalInfo: {
        __typename: 'PostalInfo' as const,
        _id: generateId(),
        postOfficeBox: EMPTY_STRING,
        postalCode: establishment.code_postal ?? EMPTY_STRING,
        region: establishment.region ?? EMPTY_STRING,
        route: route || EMPTY_STRING,
        streetNumber: establishment.numero_voie ?? EMPTY_STRING,
        additional: EMPTY_STRING,
        building: EMPTY_STRING,
        businessPostCode: null,
        locality: establishment.libelle_commune ?? EMPTY_STRING,
        inseeCode: establishment.commune ?? EMPTY_STRING,
        department: establishment.departement ?? EMPTY_STRING,
        countryAlpha2: 'FR',
      },
    },
  ];
};

export interface AddressResolutionContext {
  readonly addressIsError: boolean;
  readonly addressTypeId: Id;
  readonly hasCoordinates: boolean;
  readonly resolvedAddressForm?: AddressForm;
}

export const resolveAddresses = (
  establishment: Readonly<OrganizationSearchEstablishment>,
  {
    addressIsError,
    addressTypeId,
    hasCoordinates,
    resolvedAddressForm,
  }: Readonly<AddressResolutionContext>,
) => {
  if (!hasCoordinates || addressIsError) {
    return buildAddressFromEstablishment(establishment, { addressTypeId });
  }

  return resolvedAddressForm
    ? [{ ...resolvedAddressForm, typeIds: [addressTypeId] }]
    : undefined;
};
